import { ParsedQuery } from 'query-string';
import { Dictionary } from 'Utils/types/utility';
import { NavigationBaseData } from '../../components/Generic/NavigationItem';
import { BaseLookDetailRawData, LookImageRawData } from '../lookDetail/types';
import { GenericShopApiResponse } from 'Common/apiAdapters/types';
import {
    AppliedFilter,
    AppliedSorting,
    Filter,
    Sorting,
    SortingParameters,
} from 'Client/components/Generic/FilterAndSortBox/types';
import { PageType, ProductInterface, ProductResponse, ProductResponseMeta } from 'AppShell/appshell_types';
import { DataFetchingState } from 'Client/redux/dataFetching/generators';
import { ColorVariantsState } from 'Client/redux/colorVariants/types';
import { ObjectFit } from '@westwing/ui-kit/Picture/Image';

export interface ApiResponseAppliedFilters {
    [key: string]: {
        type: string;
        values: Array<string> | Object;
    };
}

export interface ProductsResponseData {
    filters?: Array<Filter>;
    sorting?: Array<Sorting>;
    didYouMean?: any;
    totalCount?: number;
    products?: Array<ProductResponse>;
    crossCategoryNavigation?: CrossCategoryNavigationResponse;
    appliedSorting?: AppliedSorting;
    appliedFilters?: ApiResponseAppliedFilters;
    looksInfusions?: LooksInfusionApiResponse[];
    search_response_id: string;
    search_id?: string;
    promotions: any;
    links: ProductsListLinks[];
    search_type: SearchType | undefined;
    alternativeProducts: unknown[];
    meta: ProductResponseMeta;
}

export type ProductsResponse = GenericShopApiResponse<ProductsResponseData>;
export type ProductsResponseWithGenericPromotion = ProductsResponse & {
    data: {
        promotions?: GenericPromotion[];
    };
};

export type BestsellerProductResponse = GenericShopApiResponse<ProductResponse[]>;
export type BestsellerProductResponseWithGenericPromotion = BestsellerProductResponse & {
    promotions?: GenericPromotion[];
};

export interface CrossCategoryNavigationCategoryResponse {
    id: string;
    name: string;
    image: {};
    isSelected: boolean;
    links: any[];
}

export interface CrossCategoryNavigationLinkResponse {
    rel: string;
    href: string;
}

export interface CrossCategoryNavigationResponse {
    categories: CrossCategoryNavigationCategoryResponse[];
    parentCategory: { isRoot: boolean; links: CrossCategoryNavigationLinkResponse[] };
}

export interface CrossCategoryNavigationParentCategory {
    isRoot: boolean;
    link: string;
}

export interface CrossCategoryNavigation {
    categories: NavigationBaseData[];
    parentCategory: CrossCategoryNavigationParentCategory;
}

export interface ProductsListLinks {
    rel: string;
    href: string;
}

export enum SecondaryProductsIdentifier {
    ALL_PRODUCTS = 'allProducts',
    NEW_PRODUCTS = 'newProducts',
}

export enum SearchType {
    EMPTY = '',
    FUZZY = 'fuzzy',
    STRICT = 'strict',
}

export interface BasicProductsInterface {
    products: ProductInterface[];
    alternativeProducts: ProductInterface[];
    isFetching: boolean;
    isFetchingComplete?: boolean;
    didInvalidate: boolean;
    error?: any;
}

export interface ProductsState extends BasicProductsInterface {
    didYouMean: ReducedDidYouMeanInterface[];
    filters: Filter[];
    sorting: Sorting[];
    crossCategoryNavigation: CrossCategoryNavigation;
    pageTitle: string;
    cmsPageTitle: string;
    totalCount: number;
    identifier: string;
    links: ProductsListLinks[];
    pdpUrl: string;
    searchType: SearchType;
    isMoodImageHovered?: boolean;
    plpGridColumnView?: ColumnLayoutVariants;
    looksInfusions: LooksInfusion[] | null;
    promotions?: GenericPromotion[];
    trackingData?: {
        searchId: string;
        searchResponseId: string; // NDCG tracking data
    };
    colorVariants: Record<string, ColorVariantsState>;
}

export interface GenericProductsState extends DataFetchingState {
    products: ProductInterface[];
    promotions?: GenericPromotion[];
    error?: any;
}

export interface SecondaryProductsState {
    products: { [identifier in SecondaryProductsIdentifier]: GenericProductsState };
}

export interface DidYouMeanInterface {
    title: string;
    links: LinkResponse[];
}

export interface ReducedDidYouMeanInterface {
    title: string;
    relLink: string;
}

export interface LinkResponse extends CrossCategoryNavigationLinkResponse {}

export interface HydratedFilterValue {
    id_catalog_brand: string;
    name: string;
    url_key: string;
}

export enum pathFilterNames {
    colors = 'facet_col',
    materials = 'facet_mat_filter',
    brands = 'facet_brand_keys',
    categories = 'facet_category_keys',
    sizes = 'facet_generic_size',
    styles = 'facet_styles',
    pattern = 'facet_pattern',
    form = 'facet_form',
    pillowType = 'facet_filter_pillow_type',
    lightSocketType = 'facet_light_socket_type',
    feature = 'facet_filter_feature_type',
    towelType = 'facet_filter_towels_type',
}

export interface FiltersWithHydratedValues extends Filter {
    hydratedValues: Dictionary<HydratedFilterValue>;
}

export interface BrandsFilters extends FiltersWithHydratedValues {}
export interface CategoryFilters extends FiltersWithHydratedValues {}

export const isBrandsFilter = (input: Filter): input is BrandsFilters => input.name === pathFilterNames.brands;
export const isCategoriesFilter = (input: Filter): input is CategoryFilters =>
    input.name === pathFilterNames.categories;

export enum FilterType {
    query = 'query',
    materials = 'materials',
    colors = 'colors',
    brands = 'brands',
    styles = 'styles',
    categories = 'categories',
    sizes = 'sizes',
    pattern = 'pattern',
    form = 'form',
    pillowType = 'pillowType',
    lightSocketType = 'lightSocketType',
    feature = 'feature',
    towelType = 'towelType',
}

export interface ListingPageUrlState {
    appliedFilters: AppliedFilter<FilterType>[];
    listingPageKey: string;
    listingPageType: PageType;
    sortingParams: SortingParameters;
    search?: string;
    strictSearch?: string;
    query: ParsedQuery;
    page: number;
}
export interface PriceFormatConfigOptions {
    countryCode: string;
    currency: string;
}

export interface Options extends PriceFormatConfigOptions {
    includeLooksInfusions: boolean;
    includePromotions?: boolean;
    sortingParameters: SortingParameters;

    enableInterleaving?: boolean;
    interleaveByDefaultSortFirst?: boolean;
    loginHash?: string;
    p100Plp120PerPage?: boolean;
}

export interface LooksInfusionApiResponse extends BaseLookDetailRawData {
    relatedSku: string;
    genericImagePortrait: LookImageRawData;
}

interface ContentfulRevisionType {
    createdAt: string;
    updatedAt: string;
}

export interface ContentfulBaseType {
    id: string;
    title: string;
}

export interface ContentfulImage extends ContentfulBaseType {
    description: string;
    file: {
        url: string;
        details: {
            size: number;
            image: {
                width: number;
                height: number;
            };
        };
        seo_url?: string;
        fileName: string;
        contentType: string;
    };
}

export type ImageOrientation = 'LANDSCAPE' | 'PORTRAIT';

export interface MinimalContentfulImage {
    description: string;
    title: string;
    url: string;
}

export interface GenericPromotion extends ContentfulBaseType, ContentfulRevisionType {
    headline: string;
    price: string;
    ctaText: string;
    url: string;
    backgroundColor: string;
    textColor: string;
    image: ContentfulImage;
    position: number;
    trackingName: string;
    objectFit?: ObjectFit;
}

export type GenericPromotionApiResponse = GenericShopApiResponse<{ infusions: GenericPromotion[] }>;

type ProductOrPromotion =
    | (ProductInterface & {
          isPromotion?: false;
          trackingFn?: () => void;
      })
    | (GenericPromotion & {
          isPromotion: true;
      });

export type ProductsWithPromotions = ProductOrPromotion[];

export interface LooksInfusion {
    relatedSku: string;
    portraitImg: string;
    name: string;
    slug: string;
    color: string;
    trackingName: string;
    caption: string;
    isLandscape: boolean;
}

export enum ColumnLayoutVariants {
    THREE = 3,
    FOUR = 4,
}

export interface FetchSimilarProductsQueryParams {
    limit: number;
    algorithm: string;
}

export * from 'AppShell/appshell_types/PDPProduct';
